import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { SINGLE_REMINDERS, HOLIDAY_REMINDERS, HOLIDAY_IDS, migrateAssignment, alreadyRunThisYear, anniversaryReached, withinDueWindow, DUE_WINDOW_DAYS, customIntervalDue, soldDateForContact, effectiveAssigned, holidayAnchor, reminderAnchor, NEW_CLOSING_MIN_DAYS, NEW_CLOSING_MAX_DAYS, daysSinceYMD } from './assignmentUtils';
import { scopedKey, DEFAULT_SYSTEM_MESSAGES } from './scope';
import { resolveAttachments } from './fileLib';
import { isDevMode } from './dev';
import { api, usingApi } from './api';
import Icon from './Icon';
import './RunReminders.css';

const API = '';   // same-origin in prod; dev proxies /api → :3001 (client/package.json "proxy")

function fmtDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

// Month/day (0-based month) of a 'YYYY-MM-DD' string.
function monthDay(ymd) {
  if (!ymd) return null;
  const [, m, d] = ymd.split('-').map(Number);
  return { month: m - 1, day: d };
}

// 1–31 as Chinese numerals (一, 十, 十一, 二十一, 三十一, …).
function chineseNum(n) {
  const d = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
  if (n <= 10) return d[n];
  if (n < 20) return '十' + d[n - 10];
  const tens = Math.floor(n / 10), ones = n % 10;
  return d[tens] + '十' + (ones ? d[ones] : '');
}

// A birthday's month + day worded for the contact's language: "July 3", "七月三号", "3 de julio".
function formatBirthday(birthday, lang) {
  if (!birthday) return '';
  const [, mm, dd] = birthday.split('-').map(Number);
  if (!mm || !dd) return '';
  if (lang === 'zh') return `${chineseNum(mm)}月${chineseNum(dd)}号`;
  const locale = lang === 'es' ? 'es-ES' : 'en-US';
  return new Date(2020, mm - 1, dd).toLocaleDateString(locale, { month: 'long', day: 'numeric' });
}

// The yearly anchor for an annual reminder, given its resolved anchor id
// (a built-in id, or the built-in a copy mirrors). Null for plain customs.
function anchorFor(anchorId, contact, soldDate) {
  if (!anchorId) return null;
  if (anchorId === 'happy-birthday')   return monthDay(contact.birthday);
  if (anchorId === 'sale-anniversary') return monthDay(soldDate);
  const ha = holidayAnchor(anchorId, new Date().getFullYear());   // fixed-/computed-date holidays
  if (ha) return ha;
  return null;
}

// Reminder bodies are HTML (rich text). This strips tags to a plain-text fallback
// for the email's text/plain part and for emptiness checks.
export function htmlToText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&#39;/gi, "'").replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Reminder bodies may contain variable "pills" (<span class="re-var" data-var="{{X}}">…</span>)
// inserted by the rich editor. Before substitution, replace each pill with its raw
// {{token}} so only the value is kept (the pill formatting is dropped). Plain {{token}}
// text (legacy) passes through untouched.
export function tokensToRaw(html) {
  const s = String(html || '');
  if (s.indexOf('re-var') === -1) return s;
  try {
    const div = document.createElement('div');
    div.innerHTML = s;
    div.querySelectorAll('span.re-var').forEach(span => {
      span.replaceWith(document.createTextNode(span.getAttribute('data-var') || ''));
    });
    return div.innerHTML;
  } catch {
    return s.replace(/<span[^>]*\bdata-var="([^"]*)"[^>]*>[\s\S]*?<\/span>/g, '$1');
  }
}

// Append the sending agent's saved email signature (HTML) to a message body, with a
// blank-line separator. No-op when there's no real signature. Shared by the reminder
// engine (uses the owning agent's signature) and the one-off message composer.
export function appendSignature(bodyHtml, sigHtml) {
  const sig = String(sigHtml || '');
  if (!htmlToText(sig).trim()) return bodyHtml || '';
  return `${bodyHtml || ''}<br><br>${sig}`;
}

// Localized website CTA for built-in reminders (mirrors the server). '' when no site.
function websiteCta(website, lang) {
  const s = String(website || '').trim();
  if (!s) return '';
  const url = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  const t = lang === 'zh' ? { pre: '您随时可以联系我，或访问', link: '我的网站', post: '了解更多。' }
    : lang === 'es' ? { pre: 'Siempre puedes contactarme o visitar ', link: 'mi sitio web', post: '.' }
    : { pre: 'You can always reach me or learn more on ', link: 'my website', post: '.' };
  return `<p style="margin-top:16px">${t.pre}<a href="${url}">${t.link}</a>${t.post}</p>`;
}

// Build the list of reminder emails to send from everything in localStorage.
// Normal scheduling gates always apply (annual once-per-year, custom intervals).
// Ordinal for a positive integer (1→1st, 2→2nd, 3→3rd, 11→11th, 21→21st…).
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
// Nth home-sale anniversary as of `year` (e.g. sold 2020, year 2026 → 6). null if N/A.
function annivYears(soldDate, year) {
  if (!soldDate) return null;
  const y = new Date(soldDate).getFullYear();
  if (isNaN(y)) return null;
  const n = year - y;
  return n > 0 ? n : null;
}

// `optedOutIds` — contacts who unsubscribed from automated greetings (server truth,
// fetched from /api/engagement). They're pre-skipped here so the panel is honest;
// the server independently refuses the send (409) as the hard guarantee.
export function buildJobs(scope, optedOutIds = new Set()) {
  let contacts = [], assignments = {}, customTemplates = [], messages = {}, properties = [];
  try { contacts = JSON.parse(localStorage.getItem(scopedKey('ira_contacts', scope)) || '[]'); } catch {}
  try { assignments = JSON.parse(localStorage.getItem(scopedKey('ira_assignments', scope)) || '{}'); } catch {}
  try { customTemplates = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', scope)) || '[]'); } catch {}
  try { messages = JSON.parse(localStorage.getItem(scopedKey('ira_templates', scope)) || '{}'); } catch {}
  try { properties = JSON.parse(localStorage.getItem(scopedKey('ira_properties', scope)) || '[]'); } catch {}
  let autoMap = {};
  try { autoMap = JSON.parse(localStorage.getItem(scopedKey('ira_autoenroll', scope)) || '{}'); } catch {}
  let attachMap = {};
  try { attachMap = JSON.parse(localStorage.getItem(scopedKey('ira_attachments', scope)) || '{}'); } catch {}
  // Every contact belongs to an agent — the scope IS that agent's account id, so
  // {{Agent}} resolves to their display name (alias or username).
  let agentName = '';
  let agentSignature = '';
  let agentWebsite = '';
  try {
    const accounts = JSON.parse(localStorage.getItem('ira_accounts') || '[]');
    const owner = accounts.find(a => a.id === scope);
    if (owner) { agentName = owner.alias || owner.username || ''; agentSignature = owner.signature || ''; agentWebsite = owner.website || ''; }
  } catch {}

  const labels = {
    'new-closing': 'New Closing',
    'sale-anniversary': 'Sale Anniversary',
    'happy-birthday': 'Happy Birthday',
  };
  HOLIDAY_REMINDERS.forEach(h => { labels[h.id] = h.label; });
  // customTemplates also holds built-in stamp entries ({id, builtin:true}); only the
  // REAL custom reminders belong here — otherwise built-ins get processed twice
  // (duplicate emails) and their label gets clobbered to the raw id.
  const realCustom = customTemplates.filter(c => !c.builtin && !c.base);
  realCustom.forEach(c => { labels[c.id] = c.label; });
  const reminderIds = [...SINGLE_REMINDERS.map(r => r.id), ...HOLIDAY_IDS, ...realCustom.map(c => c.id)];

  const jobs = [];
  for (const c of contacts) {
    const asgn = migrateAssignment(assignments[c.id]);
    const fullName = `${c.firstName} ${c.lastName}`.trim();
    const lang = c.language || 'en';
    const soldDate = soldDateForContact(c.id, properties);   // Sale Anniversary anchor comes from linked property
    const prop = properties.find(p => Array.isArray(p.linkedContacts) && p.linkedContacts.some(lc => String(lc.id) === String(c.id))) || {};
    const propAddress = [prop.address, [prop.city, prop.state, prop.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    for (const rid of reminderIds) {
      if (!effectiveAssigned(asgn[rid], autoMap, rid, c, soldDate)) continue;
      const detail = asgn[rid] || {};
      // Resolve the scheduling anchor: built-ins anchor to themselves, a copy of a
      // built-in carries `anchor`, plain customs are null (interval-driven).
      const anchorId = reminderAnchor(rid, realCustom);
      const label = labels[rid] || rid;
      const job = {
        id: `${c.id}__${rid}`,
        contactId: c.id,
        reminderId: rid,
        contactName: fullName || '(no name)',
        email: c.email || '',
        reminderLabel: label,
        subject: label,
        status: 'pending',
        note: '',
        lastRunAt: detail.lastRunAt || '',
      };
      if (!c.email) { job.status = 'skipped'; job.note = 'no email'; jobs.push(job); continue; }
      // Contact unsubscribed from automated greetings — never attempted (the ✋ badge
      // in Contacts shows the same state; a personal Message Contacts still works).
      if (optedOutIds.has(String(c.id))) {
        job.status = 'skipped'; job.note = 'unsubscribed from automated greetings'; jobs.push(job); continue;
      }
      // Happy Birthday (and copies of it) need the contact's birthday — the
      // assignment stays inactive until one is set, even in Dev mode.
      if (anchorId === 'happy-birthday' && !c.birthday) {
        job.status = 'skipped'; job.note = 'no birthday set'; jobs.push(job); continue;
      }
      // Sale Anniversary / New Closing (and copies) need a sold date from a linked property.
      if ((anchorId === 'sale-anniversary' || anchorId === 'new-closing') && !soldDate) {
        job.status = 'skipped'; job.note = 'no sold property linked'; jobs.push(job); continue;
      }
      let body = tokensToRaw(messages[`${rid}__${lang}`] || messages[`${rid}__en`] || '');
      if (!htmlToText(body).trim() && anchorId) {   // built-in default fallback (no stored override at this scope)
        const def = DEFAULT_SYSTEM_MESSAGES[anchorId];
        if (def) body = tokensToRaw(def[lang] || def.en || '');
      }
      if (!htmlToText(body).trim()) { job.status = 'skipped'; job.note = 'no message template'; jobs.push(job); continue; }
      // New Closing: ONE-TIME, 7–30 days after the close date. Old closings imported
      // as history fall outside the window and are skipped for good. (Mirrors the server.)
      if (anchorId === 'new-closing') {
        const ds = daysSinceYMD(soldDate);
        if (ds === null || ds < NEW_CLOSING_MIN_DAYS) {
          job.status = 'skipped'; job.note = `sends ${NEW_CLOSING_MIN_DAYS}–${NEW_CLOSING_MAX_DAYS} days after closing`; jobs.push(job); continue;
        }
        if (ds > NEW_CLOSING_MAX_DAYS) {
          job.status = 'skipped'; job.note = `closed ${ds} days ago — past the ${NEW_CLOSING_MAX_DAYS}-day window`; jobs.push(job); continue;
        }
        // Dedupe per SALE (its sold date) — not by send time, so a second closing
        // still in the window fires even after an earlier send (mirrors the server).
        if (detail.lastOccurrenceKey === soldDate) {
          job.status = 'skipped'; job.note = 'already sent for this closing'; jobs.push(job); continue;
        }
        job.occurrenceKey = soldDate;   // recordRun stamps this after a successful send
      }
      // Annual reminders fire on the anniversary date, once per year. Dev fires every time.
      const anchor = anchorId === 'new-closing' ? null : anchorFor(anchorId, c, soldDate);
      if (anchor) {
        if (!anniversaryReached(anchor.month, anchor.day)) {
          job.status = 'skipped'; job.note = 'not due yet (before anniversary)'; jobs.push(job); continue;
        }
        if (!withinDueWindow(anchor.month, anchor.day)) {
          job.status = 'skipped'; job.note = `past the ${DUE_WINDOW_DAYS}-day window`; jobs.push(job); continue;
        }
        if (alreadyRunThisYear(detail.lastRunAt, anchor.month, anchor.day)) {
          job.status = 'skipped'; job.note = 'already sent this year'; jobs.push(job); continue;
        }
        // Year zero: closed THIS year → no anniversary yet (New Closing covers it).
        if (anchorId === 'sale-anniversary' && annivYears(soldDate, new Date().getFullYear()) == null) {
          job.status = 'skipped'; job.note = 'closed this year — no anniversary yet'; jobs.push(job); continue;
        }
      }
      // PLAIN custom reminders (no anchor) fire by their saved interval. Anchored
      // copies use the annual anchor logic above instead. Dev fires every time.
      if (rid.startsWith('custom__') && !anchorId && !customIntervalDue(detail.lastRunAt, detail.interval)) {
        const iv = detail.interval || 'One Time';
        job.status = 'skipped';
        job.note = iv === 'One Time' ? 'already sent (one time)' : `not due yet (${iv} interval)`;
        jobs.push(job); continue;
      }
      body = body.replace(/\{\{Birthday(?: MM-DD)?\}\}/g, formatBirthday(c.birthday, lang));
      const annN = annivYears(soldDate, new Date().getFullYear());
      const subs = {
        '{{Contact}}': fullName,
        '{{First Name}}': c.firstName || '',
        '{{Last Name}}': c.lastName || '',
        '{{Email}}': c.email || '',
        '{{Phone}}': c.phone || c.mobile || '',
        '{{Sold Date}}': soldDate ? fmtDate(soldDate) : '',
        '{{Years}}': annN != null ? String(annN) : '',
        '{{Nth}}': annN != null ? ordinal(annN) : '',
        '{{Agent}}': agentName,
        '{{Property}}': propAddress,
        '{{Address}}': prop.address || '',
        '{{City}}': prop.city || '',
        '{{State}}': prop.state || '',
        '{{Zip}}': prop.zip || '',
        '{{Price}}': prop.price || '',
        // Special date / holiday system variables.
        '{{New Year}}': "New Year's Day",
        "{{Valentine's Day}}": "Valentine's Day",
        '{{July 4}}': 'the Fourth of July',
        '{{Thanksgiving}}': 'Thanksgiving',
        '{{Christmas}}': 'Christmas',
      };
      for (const [k, v] of Object.entries(subs)) body = body.split(k).join(v);
      if (anchorId) body += websiteCta(agentWebsite, lang);   // built-in reminders get the agent's website CTA
      body = appendSignature(body, agentSignature);      // the agent's signature on every reminder
      job.html = body;                                   // rich-text HTML body
      job.text = htmlToText(body);                       // plain-text fallback
      job.body = job.text;                               // legacy field
      // attachMap[rid] is an array of file-ID references — bytes are fetched from the
      // server at SEND time (resolveAttachments), not held in the job.
      job.attachmentIds = attachMap[rid] || [];
      jobs.push(job);
    }
  }
  return jobs;
}

const STATUS_ICON = { pending: '•', sending: '⏳', sent: '✅', error: '❌', skipped: '⏭️' };

function RunReminders({ scope, orgSuspended = false, selfSuspended = false, orgOverCapacity = false, mlsSuspended = false }) {
  const suspended = orgSuspended || selfSuspended || orgOverCapacity || mlsSuspended;
  // The manual send panel (Run + the "to send" preview) is a DEV-ONLY tool that
  // applies the normal scheduling gates. In production reminders go out on schedule
  // via the server. The Run History below is always visible.
  const devTools = isDevMode();
  const K_ASSIGNMENTS = scopedKey('ira_assignments', scope);
  const K_HISTORY     = scopedKey('ira_run_history', scope);
  const [jobs, setJobs]       = useState([]);
  const [phase, setPhase]     = useState('idle');   // idle | running | done
  const [current, setCurrent] = useState(-1);
  const [health, setHealth]   = useState(null);     // { ok, configured }
  const [runSummary, setRunSummary] = useState(null);   // { sent, failed } of the last run
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_HISTORY) || '[]'); } catch { return []; }
  });
  const [expandedRun, setExpandedRun] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [histSearch, setHistSearch] = useState('');
  // Server-sent history: the authoritative record of what the scheduled processor
  // actually emailed to this agent's contacts (ReminderSend). This is the primary
  // history in production, where sending happens on the server, not the browser.
  const [serverSends, setServerSends] = useState([]);
  const [serverLoading, setServerLoading] = useState(usingApi());
  const [srvSearch, setSrvSearch] = useState('');
  const stopRef = useRef(false);   // flipped true to interrupt an in-progress run

  useEffect(() => {
    fetch(`${API}/api/health`)
      .then(r => r.json())
      .then(d => setHealth({ ok: true, configured: !!d.emailConfigured }))
      .catch(() => setHealth({ ok: false, configured: false }));
  }, []);

  // Load what the server has actually sent for this agent. Refreshes on mount and
  // after a manual browser run (which may have added rows in API mode).
  useEffect(() => {
    if (!usingApi()) { setServerLoading(false); return; }
    let alive = true;
    setServerLoading(true);
    api.get('/api/reminders/history')
      .then(d => { if (alive) setServerSends(Array.isArray(d.sends) ? d.sends : []); })
      .catch(() => { if (alive) setServerSends([]); })
      .finally(() => { if (alive) setServerLoading(false); });
    return () => { alive = false; };
  }, [scope, runSummary]);

  // Stop a run in progress if the user navigates away / unmounts.
  useEffect(() => () => { stopRef.current = true; }, []);

  // Contacts who unsubscribed (server truth) — pre-skipped in the job list so the
  // panel matches what the server will actually allow.
  const [optOuts, setOptOuts] = useState(() => new Set());
  useEffect(() => {
    if (!usingApi()) return;
    let alive = true;
    api.get(`/api/engagement${scope ? `?agentId=${encodeURIComponent(scope)}` : ''}`)
      .then(d => { if (alive) setOptOuts(new Set((d.optOuts || []).map(o => String(o.contactId)))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [scope]);

  // Rebuild the job list on mount and whenever the scope (or opt-out set) changes.
  // Never mid-run: the late-arriving opt-out fetch must not clobber live statuses.
  const phaseRef = useRef('idle');
  useEffect(() => { phaseRef.current = phase; });
  useEffect(() => {
    if (phaseRef.current === 'running') return;
    setJobs(buildJobs(scope, optOuts));
    setPhase('idle');
    setCurrent(-1);
    setRunSummary(null);
  }, [scope, optOuts]);

  const sendable = jobs.filter(j => j.status === 'pending');   // still queued to send
  const skipped  = jobs.filter(j => j.status === 'skipped');
  // During a run these come from live job statuses; after a run the list is
  // rebuilt (so rows show their new due-state), so use the captured summary.
  const sentCount   = runSummary ? runSummary.sent   : jobs.filter(j => j.status === 'sent').length;
  const errorCount  = runSummary ? runSummary.failed : jobs.filter(j => j.status === 'error').length;
  const doneCount   = jobs.filter(j => ['sent', 'error', 'skipped'].includes(j.status)).length;
  const progressPct = phase === 'done' ? 100 : (jobs.length ? Math.round((doneCount / jobs.length) * 100) : 0);

  // Run-history filter: by contact name or reminder name (matches inside a run's results).
  const hq = histSearch.toLowerCase().trim();
  const resultMatches = (r) => (r.contactName || '').toLowerCase().includes(hq) || (r.reminderLabel || '').toLowerCase().includes(hq);
  const shownHistory = hq ? history.filter(h => (h.results || []).some(resultMatches)) : history;

  // Server-sent history filter (by contact or reminder name).
  const sq = srvSearch.toLowerCase().trim();
  const shownServer = sq
    ? serverSends.filter(s => (s.contactName || '').toLowerCase().includes(sq) || (s.reminderLabel || '').toLowerCase().includes(sq))
    : serverSends;
  const serverSentCount = serverSends.filter(s => s.status === 'sent').length;
  const serverFailCount = serverSends.filter(s => s.status !== 'sent').length;
  // In production the browser-run log is empty (the server sends); only surface that
  // secondary section when there's something in it or the dev tools are on.
  const showBrowserRuns = history.length > 0 || devTools;

  function saveHistory(updater) {
    setHistory(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(K_HISTORY, JSON.stringify(next));
      return next;
    });
  }
  function deleteRun(id) {
    saveHistory(prev => prev.filter(h => h.id !== id));
    setExpandedRun(e => (e === id ? null : e));
  }
  function clearAllHistory() {
    saveHistory([]);
    setExpandedRun(null);
    setConfirmClear(false);
  }

  // Persist the actual-send timestamp so the reminder isn't re-sent this cycle.
  // `occurrenceKey` (when the job carries one, e.g. New Closing's sold date) records
  // WHICH occurrence was handled, so per-sale dedupe survives a later second sale.
  function recordRun(contactId, reminderId, occurrenceKey) {
    const now = new Date().toISOString();
    let assignments = {};
    try { assignments = JSON.parse(localStorage.getItem(K_ASSIGNMENTS) || '{}'); } catch {}
    const asgn = migrateAssignment(assignments[contactId]);
    const detail = typeof asgn[reminderId] === 'object' ? asgn[reminderId] : {};
    const updated = { ...detail, lastRunAt: now, ...(occurrenceKey ? { lastOccurrenceKey: occurrenceKey } : {}) };
    // One-time PLAIN custom reminders un-assign themselves once sent (explicit opt-out
    // so they can't be re-added; lastRunAt is kept for the record). Anchored copies are
    // annual + date-anchored — never auto-unassign them.
    let customTemplates = [];
    try { customTemplates = JSON.parse(localStorage.getItem(scopedKey('ira_custom_templates', scope)) || '[]'); } catch {}
    const anchored = !!reminderAnchor(reminderId, customTemplates);
    if (reminderId.startsWith('custom__') && !anchored && (detail.interval || 'One Time') === 'One Time') {
      updated.assigned = false;
      updated.explicit = true;
    }
    asgn[reminderId] = updated;
    assignments[contactId] = asgn;
    localStorage.setItem(K_ASSIGNMENTS, JSON.stringify(assignments));
    return now;
  }

  async function run() {
    stopRef.current = false;
    const list = buildJobs(scope, optOuts);
    setJobs(list);
    setPhase('running');
    setRunSummary(null);

    let sent = 0, failed = 0;
    const results = [];
    for (let i = 0; i < list.length; i++) {
      if (stopRef.current) break;   // user hit Stop
      const job = list[i];
      if (job.status === 'skipped') continue;
      // flushSync forces each status change to paint immediately, instead of
      // React deferring the in-loop renders until the next user interaction.
      flushSync(() => {
        setCurrent(i);
        setJobs(prev => prev.map((j, idx) => idx === i ? { ...j, status: 'sending' } : j));
      });
      try {
        const res = await fetch(`${API}/api/send-reminder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: job.email, subject: job.subject, text: job.text, html: job.html, attachments: await resolveAttachments(job.attachmentIds), contactId: job.contactId, agentId: scope }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Send failed');
        const ts = recordRun(job.contactId, job.reminderId, job.occurrenceKey);
        sent++;
        results.push({ contactName: job.contactName, reminderLabel: job.reminderLabel, email: job.email, status: 'sent', note: '', at: ts });
        flushSync(() => setJobs(prev => prev.map((j, idx) => idx === i ? { ...j, status: 'sent', lastRunAt: ts } : j)));
      } catch (err) {
        failed++;
        results.push({ contactName: job.contactName, reminderLabel: job.reminderLabel, email: job.email, status: 'error', note: err.message, at: new Date().toISOString() });
        flushSync(() => setJobs(prev => prev.map((j, idx) => idx === i ? { ...j, status: 'error', note: err.message } : j)));
      }
    }
    const stopped = stopRef.current;
    // Record this run in the deletable history log.
    const skippedCount = list.filter(j => j.status === 'skipped').length;
    const entry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      at: new Date().toISOString(),
      sent, failed, skipped: skippedCount, total: list.length, stopped, results,
    };
    // Only log a run that actually did something (don't log an instant stop with nothing sent).
    if (sent + failed > 0) saveHistory(prev => [entry, ...prev]);
    // Rebuild from the now-updated assignments so each row shows its post-run
    // state (e.g. a just-sent Monthly reminder becomes "not due yet").
    flushSync(() => {
      setCurrent(-1);
      setRunSummary({ sent, failed, stopped });
      setJobs(buildJobs(scope, optOuts));
      setPhase('done');
    });
  }
  function stop() { stopRef.current = true; }

  const currentJob = current >= 0 ? jobs[current] : null;

  return (
    <div className="rr-container">
      <div className="rr-header">
        <div>
          <h2><span className="lbl-full">Reminder History</span><span className="lbl-short">History</span></h2>
          <p>Reminders iBetterAgent has emailed to your contacts on schedule.{devTools ? ' Manual send tools are below (dev).' : ''}</p>
        </div>
      </div>

      {/* PRIMARY: what the server actually sent (ReminderSend). Only in API mode —
          a local-only build has no server sends to show. */}
      {usingApi() && (
        <div className="rr-server-history">
          <div className="rr-sh-head">
            <h3><Icon name="run-reminders" size={16} /> Sent to your contacts</h3>
            {!serverLoading && serverSends.length > 0 && (
              <span className="rr-sh-counts">
                <span className="rr-hc-sent">{serverSentCount} sent</span>
                {serverFailCount > 0 && <span className="rr-hc-fail">{serverFailCount} failed</span>}
              </span>
            )}
          </div>

          {serverSends.length > 0 && (
            <input className="rr-history-search" type="text"
              placeholder="🔍 Filter by contact or reminder…"
              value={srvSearch} onChange={e => setSrvSearch(e.target.value)} />
          )}

          {serverLoading ? (
            <p className="rr-history-empty">Loading sent history…</p>
          ) : serverSends.length === 0 ? (
            <p className="rr-history-empty">Nothing sent yet. When a scheduled reminder goes out to one of your contacts, it will appear here.</p>
          ) : shownServer.length === 0 ? (
            <p className="rr-history-empty">No sends match “{srvSearch}”.</p>
          ) : (
            <div className="rr-table-wrap">
              <table className="rr-table">
                <thead>
                  <tr>
                    <th className="rr-th-status"></th>
                    <th>When</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Reminder</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {shownServer.map(s => (
                    <tr key={s.id} className={`rr-row-${s.status === 'sent' ? 'sent' : 'error'}`}>
                      <td className="rr-status-icon">{s.status === 'sent' ? '✅' : '❌'}</td>
                      <td className="rr-when" data-label="When">{fmtTime(s.at)}</td>
                      <td className="rr-contact" data-label="Contact">{s.contactName || '—'}</td>
                      <td className="rr-email" data-label="Email">{s.email || '—'}</td>
                      <td data-label="Reminder">{s.reminderLabel || '—'}</td>
                      <td className="rr-result" data-label="Result">
                        {s.status === 'sent'
                          ? <span className="rr-result-sent">Sent</span>
                          : <span className="rr-result-err" title={s.error || ''}>Failed{s.error ? `: ${s.error}` : ''}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* DEV-ONLY: manual send (Run + the "to send" preview & progress). Normal
          scheduling gates always apply — only genuinely-due reminders are sent.
          CSS `order` floats this whole panel BELOW the Run History. */}
      {devTools && <div className="rr-dev-panel">
      <div className="rr-dev-head"><Icon name="test" size={16} /> Developer — manual send</div>
      <div className="rr-header-actions">
        {phase === 'running' ? (
          <button className="rr-stop-btn" onClick={stop}><Icon name="stop" size={16} /> Stop</button>
        ) : (
          <button className="rr-run-btn" onClick={run} disabled={sendable.length === 0 || suspended}>
            <Icon name="launch" size={16} /> <span className="lbl-full">Run Reminders</span><span className="lbl-short">Run</span>
          </button>
        )}
      </div>
      {suspended && (
        <div className="rr-suspended-banner">
          {orgOverCapacity
            ? '⛔ Reminders are paused — your organization is over its seat limit. Your admin needs to free up seats to resume.'
            : mlsSuspended
              ? '⏸ Reminder processing is currently stopped for your MLS by iBetterAgent. It will resume when processing is turned back on.'
              : orgSuspended
                ? '⏸ Your organization has paused all reminder sending (maintenance). Contact your admin to resume.'
                : '⏸ Your reminders are paused — resume with “Pause Reminders” on the Reminder Templates page.'}
        </div>
      )}

      {health && (
        <div className={`rr-health ${health.ok && health.configured ? 'ok' : 'warn'}`}>
          {!health.ok
            ? '⚠ Mail server not reachable on :3001 — start the server (npm run dev) before sending.'
            : health.configured
              ? '✓ Mail server connected and email is configured.'
              : '⚠ Mail server reachable, but SMTP is not configured (set SMTP_USER / SMTP_PASS in .env).'}
        </div>
      )}

      <div className="rr-summary">
        <span><strong>{sendable.length}</strong> to send</span>
        {skipped.length > 0 && <span className="rr-summary-skip">{skipped.length} skipped</span>}
        {phase === 'running' && <span className="rr-summary-sent">{sentCount} sent</span>}
        {phase === 'running' && errorCount > 0 && <span className="rr-summary-err">{errorCount} failed</span>}
      </div>

      {phase !== 'idle' && (
        <div className="rr-status">
          <div className="rr-progress-track">
            <div className="rr-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="rr-status-line">
            {phase === 'running' && currentJob
              ? <>⏳ Sending <strong>{currentJob.reminderLabel}</strong> to <strong>{currentJob.contactName}</strong> &lt;{currentJob.email}&gt; — {doneCount + 1} of {jobs.length}…</>
              : phase === 'done'
                ? <>{runSummary && runSummary.stopped ? '■ Stopped' : '✅ Done'} — sent {sentCount}{errorCount ? `, ${errorCount} failed` : ''}.</>
                : null}
          </div>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="rr-empty">
          <span><Icon name="reminder-assignments" size={20} /></span>
          <p>No assigned reminders to send. Assign reminders to contacts first.</p>
        </div>
      ) : (
        <div className="rr-table-wrap">
          <table className="rr-table">
            <thead>
              <tr>
                <th className="rr-th-status"></th>
                <th>Contact</th>
                <th>Email</th>
                <th>Reminder</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j, idx) => (
                <tr key={j.id} className={`${current === idx ? 'rr-row-active' : ''} rr-row-${j.status}`}>
                  <td className="rr-status-icon">{STATUS_ICON[j.status]}</td>
                  <td className="rr-contact" data-label="Contact">{j.contactName}</td>
                  <td className="rr-email" data-label="Email">{j.email || '—'}</td>
                  <td data-label="Reminder">{j.reminderLabel}</td>
                  <td className="rr-result" data-label="Result">
                    {j.status === 'sent' && <span className="rr-result-sent">Sent {fmtTime(j.lastRunAt)}</span>}
                    {j.status === 'sending' && <span className="rr-result-sending">Sending…</span>}
                    {j.status === 'error' && <span className="rr-result-err">Failed: {j.note}</span>}
                    {j.status === 'skipped' && (
                      j.note === 'already sent this year'
                        ? <span className="rr-result-skip">Already sent {fmtTime(j.lastRunAt)}</span>
                        : <span className="rr-result-skip">Skipped: {j.note}</span>
                    )}
                    {j.status === 'pending' && <span className="rr-result-pending">Queued</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>}

      {showBrowserRuns && <div className="rr-history">
        <div className="rr-manual-title"><Icon name="test" size={14} /> Manual runs from this browser</div>
        {history.length > 0 && (
          <div className="rr-history-head">
            {confirmClear ? (
              <span className="rr-history-confirm">
                Delete all run history?
                <button className="rr-history-clear-yes" onClick={clearAllHistory}>Clear All</button>
                <button className="contacts-import-cancel-btn" onClick={() => setConfirmClear(false)}>Cancel</button>
              </span>
            ) : (
              <button className="rr-history-clear" onClick={() => setConfirmClear(true)}><Icon name="delete" size={16} /> Clear All</button>
            )}
          </div>
        )}

        {history.length > 0 && (
          <input className="rr-history-search" type="text"
            placeholder="🔍 Filter history by contact or reminder…"
            value={histSearch} onChange={e => setHistSearch(e.target.value)} />
        )}

        {history.length === 0 ? (
          <p className="rr-history-empty">No past runs yet. Completed runs are logged here, and you can delete them anytime.</p>
        ) : shownHistory.length === 0 ? (
          <p className="rr-history-empty">No runs match “{histSearch}”.</p>
        ) : (
          <ul className="rr-history-list">
            {shownHistory.map(h => (
              <li key={h.id} className="rr-history-item">
                <div className="rr-history-row">
                  <button className="rr-history-toggle" onClick={() => setExpandedRun(e => (e === h.id ? null : h.id))}
                    title={expandedRun === h.id ? 'Collapse' : 'Show details'}>
                    {expandedRun === h.id ? <Icon name="chevron-down" size={14} /> : <Icon name="chevron-right" size={14} />}
                  </button>
                  <span className="rr-history-when">{fmtTime(h.at)}</span>
                  <span className="rr-history-counts">
                    <span className="rr-hc-sent">{h.sent} sent</span>
                    {h.failed > 0 && <span className="rr-hc-fail">{h.failed} failed</span>}
                    {h.skipped > 0 && <span className="rr-hc-skip">{h.skipped} skipped</span>}
                    {h.stopped && <span className="rr-hc-stopped">Stopped</span>}
                  </span>
                  <button className="rr-history-del" title="Delete this run from history"
                    onClick={() => deleteRun(h.id)}><Icon name="delete" size={16} /></button>
                </div>
                {expandedRun === h.id && (
                  <div className="rr-history-detail">
                    {(!h.results || h.results.length === 0) ? (
                      <span className="rr-history-empty">No messages were sent in this run.</span>
                    ) : (
                      <table className="rr-history-table">
                        <tbody>
                          {(hq ? h.results.filter(resultMatches) : h.results).map((r, i) => (
                            <tr key={i}>
                              <td className="rr-status-icon">{STATUS_ICON[r.status]}</td>
                              <td>{r.contactName}</td>
                              <td className="rr-email">{r.email || '—'}</td>
                              <td>{r.reminderLabel}</td>
                              <td className="rr-result">
                                {r.status === 'sent'
                                  ? <span className="rr-result-sent">Sent {fmtTime(r.at)}</span>
                                  : <span className="rr-result-err">Failed: {r.note}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>}
    </div>
  );
}

export default RunReminders;
